<template>
  <v-row style="border: 1px solid gray; margin-bottom: 30px">
    <v-col cols="12">
      <div>
        <h1>{{ title }}</h1>
        <h3>{{ subtitle }}</h3>
      </div>
      <v-btn
        rounded
        depressed
        :loading="buttonLoading"
        color="primary"
        @click="downloadList()"
        ><span>Download</span></v-btn
      >

      <v-progress-linear
        v-if="isProcessing"
        :value="progress"
        height="25"
        striped
      >
        <template v-slot:default="{ value }">
          <strong>
            {{ Math.ceil(value || 0) }}% ({{ processedRows || 0 }}/{{
              totalRows || 0
            }})
          </strong>
        </template>
      </v-progress-linear>
    </v-col>

    <v-col cols="12">
      <v-progress-circular
        v-if="isPolling"
        indeterminate
        color="primary"
      ></v-progress-circular>
      <div v-if="lastJobFinishedSuccessfully">
        <h1>Last batch with ID {{ lastBatchId }} finished successfully</h1>
        <v-btn
          v-if="fileURL"
          rounded
          depressed
          color="primary"
          :href="fileURL"
          target="_blank"
          ><span>Download File</span></v-btn
        >
      </div>
    </v-col>
  </v-row>
</template>

<script>
  import {
    DOWNLOAD_LIST_MUTATION,
    POLL_DOWNLOAD_LIST_STATUS_MUTATION,
  } from '../../constants/graphql/daily-tasks';
  export default {
    name: 'DownloadList',
    props: {
      title: {
        type: String,
        required: true,
      },
      subtitle: {
        type: String,
        required: true,
      },
      type: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        buttonLoading: false,
        intervalId: null,
        downloadJobChanges: null,
        isProcessing: false,
        lastJobFinishedSuccessfully: false,
        lastBatchId: null,
        isPolling: false,
        progress: 0,
        totalRows: 0,
        processedRows: 0,
        isInitialPoll: true,
        fileURL: null,
      };
    },
    created() {
      this.pollStatus();
      this.startPolling();
    },
    methods: {
      downloadList() {
        this.lastJobFinishedSuccessfully = false;
        this.fileURL = null;
        this.buttonLoading = true;
        const type = this.type;

        this.$apollo
          .mutate({
            mutation: DOWNLOAD_LIST_MUTATION,
            variables: {
              type,
            },
          })
          .then(_response => {
            this.downloadJobChanges = _response.data.downloadList;
            if (this.downloadJobChanges.errorMessage) {
              alert(this.downloadJobChanges.errorMessage);
              this.buttonLoading = false;
              return;
            }
            // If status is already completed, handle it immediately
            if (this.downloadJobChanges.status === 'completed') {
              this.isProcessing = false;
              this.buttonLoading = false;
              this.lastJobFinishedSuccessfully = true;
              this.lastBatchId = this.downloadJobChanges.id;
              if (this.downloadJobChanges.fileURL) {
                this.fileURL = this.downloadJobChanges.fileURL;
              }
              return;
            }

            // Store fileURL if available in initial response (for pending/processing jobs)
            if (this.downloadJobChanges.fileURL) {
              this.fileURL = this.downloadJobChanges.fileURL;
            }

            if (this.intervalId) {
              this.stopPolling();
            }
            this.startPolling();
            this.buttonLoading = false;
            if (this.downloadJobChanges.totalRows > 0) {
              this.isProcessing = true;
              this.totalRows = this.downloadJobChanges.totalRows;
              this.processedRows = this.downloadJobChanges.processedRows;
              this.progress =
                (this.downloadJobChanges.processedRows /
                  this.downloadJobChanges.totalRows) *
                100;
            }
          })
          .catch(error => {
            this.buttonLoading = false;
            alert(`Error: ${error.message}`);
          });
      },
      startPolling() {
        this.intervalId = setInterval(this.pollStatus, 5000);
      },
      stopPolling() {
        if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = null;
        }
        this.isProcessing = false;
        this.buttonLoading = false;
        this.isPolling = false;
        this.isInitialPoll = true;
      },
      pollStatus() {
        const type = this.type;

        if (this.isInitialPoll) {
          this.isPolling = true;
        }

        this.$apollo
          .mutate({
            mutation: POLL_DOWNLOAD_LIST_STATUS_MUTATION,
            variables: {
              type,
            },
          })
          .then(_response => {
            const status = _response.data.pollDownloadListStatus;
            if (!status) {
              this.stopPolling();
              this.downloadJobChanges = null;
              this.isProcessing = false;
              this.buttonLoading = false;
              return;
            }

            this.isProcessing =
              status.status !== 'completed' &&
              status.status !== 'failed' &&
              status.totalRows > 0;

            if (this.isProcessing) {
              this.totalRows = status.totalRows;
              this.processedRows = status.processedRows;
              this.progress = (status.processedRows / status.totalRows) * 100;
            }

            if (status.status === 'completed') {
              this.isProcessing = false;
              this.buttonLoading = false;
              this.downloadJobChanges = status;
              this.lastJobFinishedSuccessfully = true;
              this.lastBatchId = status.id;
              this.fileURL = status.fileURL;
            } else if (status.status === 'failed') {
              this.stopPolling();
              this.buttonLoading = false;
              alert('Processing failed: ' + status.errorMessage);
            }
          })
          .catch(error => {
            if (
              error.message === 'GraphQL error: No download list jobs found'
            ) {
              this.stopPolling();
              return;
            }
            this.stopPolling();
          })
          .finally(() => {
            if (this.isInitialPoll) {
              this.isPolling = false;
              this.isInitialPoll = false;
            }
          });
      },
    },
    beforeDestroy() {
      this.stopPolling();
    },
  };
</script>
